import { useEffect, useState } from "react";
import { api, formatDate } from "../lib/api";
import { PageHeader, StatCard, SectionCard, Stars } from "../components/Shared";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import { Star, Clock, MessageCircle } from "lucide-react";

const CATS = ["quality", "communication", "delivery", "value"];

export default function Performance() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/performance").then((r) => setData(r.data)).catch(() => setData({ overall_rating: 0, rating_breakdown: {}, reviews: [], response_rate: 0, avg_response_time_hours: 0, total_rfqs: 0 })); }, []);

  if (!data) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title="Performance" subtitle="Your ratings, reviews, and responsiveness metrics" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-zinc-200 rounded-md p-6 text-center" data-testid="overall-rating">
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Overall rating</div>
          <div className="text-6xl font-bold font-outfit text-[#6366F1] mt-3">{data.overall_rating}</div>
          <div className="mt-2 flex justify-center"><Stars value={data.overall_rating} size={20} /></div>
          <div className="text-xs text-zinc-500 mt-2">Based on {(data.reviews || []).length} reviews</div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-md p-6 lg:col-span-2">
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-4">Rating breakdown</div>
          <div className="space-y-3">
            {CATS.map((c) => (
              <div key={c}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="capitalize">{c}</span>
                  <span className="font-semibold">{(data.rating_breakdown?.[c] || 0).toFixed(1)}</span>
                </div>
                <Progress value={(data.rating_breakdown?.[c] || 0) * 20} className="h-2" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Response Rate" value={`${data.response_rate}%`} icon={MessageCircle} accent="#10B981" />
        <StatCard label="Avg Response Time" value={`${data.avg_response_time_hours}h`} icon={Clock} accent="#3B82F6" />
        <StatCard label="Total RFQs" value={data.total_rfqs} icon={Star} accent="#F59E0B" />
      </div>

      <SectionCard title="Customer reviews">
        {(data.reviews || []).length === 0 && <div className="text-sm text-zinc-500 text-center py-8">No reviews yet</div>}
        <div className="space-y-3">
          {(data.reviews || []).map((r) => (
            <div key={r.id} className="border border-zinc-100 rounded-md p-4" data-testid="perf-review">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm">{r.buyer_name.replace(/(.{2}).+/, "$1***")}</div>
                  <Stars value={r.rating} />
                </div>
                <div className="text-xs text-zinc-500">{formatDate(r.created_at)}</div>
              </div>
              <p className="text-sm text-zinc-700 mt-2">{r.comment}</p>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
